import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AlertsService, ModalService } from '@clavisco/alerts';
import { ICLCallbacksInterface, CL_CHANNEL, LinkerService, Register, StepDown, Run, ICLEvent } from '@clavisco/linker';
import { IMenuNode } from '@clavisco/menu';
import { IPermission } from 'src/app/core/interfaces/i-permission';
import { IToken } from 'src/app/core/interfaces/i-token';
import { DataStorageService } from 'src/app/core/services/data-storage.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { Structures } from '@clavisco/core';
import ICLResponse = Structures.Interfaces.ICLResponse;

/**
 * MenuComponent manages the application's menu, including user permissions, menu visibility, and navigation.
 */
@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit {

  /** ID of the menu component. */
  MenuId: string = 'menuId';

  /** Path to the logo image. */
  Logo: string = './../../../assets/Logo-Claviscobck.png';

  /** Flag indicating whether the user image is available. */
  userImageAvailable: boolean = true;

  /** Path to the default user image. */
  userImageUrl: string = 'assets/user.png';

  /** Title displayed in the menu. */
  Title: string = 'Bienvenido';

  /** Current user information. */
  User!: IToken;

  /** Flag indicating whether the menu is toggled. */
  Toggle: Boolean = true;

  /** List of menu nodes representing menu items. */
  MenuNodes: IMenuNode[] = [];

  /** Callbacks for handling menu events. */
  callbacks: ICLCallbacksInterface<CL_CHANNEL> = {
    Callbacks: {},
    Tracks: []
  };

  /** List of permissions for the current user. */
  permsList: IPermission[] = [];

  /** Text representing the company name. */
  txtcompany: string = 'company';

  /** Text representing the current action. */
  txtaccion: string = '';

  /**
   * Constructs the MenuComponent.
   *
   * @param {Router} router - Angular Router for navigation.
   * @param {LinkerService} linkerService - Service for managing linker operations.
   * @param {DataStorageService} dataStorageService - Service for managing data storage.
   * @param {PermissionService} permsService - Service for managing permissions.
   * @param {AlertsService} alertService - Service for displaying alerts and notifications.
   * @param {MatDialog} dialog - Angular Material dialog service for opening dialogs.
   * @param {ChangeDetectorRef} cdr - Service for detecting changes in the component.
   * @param {ModalService} modalService - Service for displaying modals and alerts.
   */
  constructor(
    private router: Router,
    @Inject('LinkerService') private linkerService: LinkerService,
    private dataStorageService: DataStorageService,
    private permsService: PermissionService,
    private alertService: AlertsService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
  ) {
    this.MenuNodes = [
      {
        Key: "home",
        Description: "Inicio",
        Icon: "house",
        Route: "/home",
        Visible: true,
        Nodes: []
      },
      {
        Key: "documents",
        Description: "Documentos",
        Icon: "article",
        Route: "",
        Visible: false,
        Nodes: [
          {
            Key: "documents-list",
            Description: "Lista de Documentos",
            Icon: "",
            Route: "/documents-list",
            Visible: false,
            Nodes: []
          }
        ]
      },
      {
        Key: "logs",
        Description: "Historial Logs",
        Icon: "history",
        Route: "",
        Visible: false,
        Nodes: [
          {
            Key: "logs-documents",
            Description: "Historial Documentos",
            Icon: "",
            Route: "/logs-documents",
            Visible: false,
            Nodes: []
          },
          {
            Key: "logsViewer",
            Description: "Logs Físicos",
            Icon: "",
            Route: "/logsViewer",
            Visible: false,
            Nodes: []
          }
        ]
      },
      {
        Key: "settings",
        Description: "Configuración",
        Icon: "settings_suggest",
        Route: "",
        Visible: false,
        Nodes: [
          {
            Key: "users",
            Description: "Usuarios",
            Icon: "",
            Route: "/users",
            Visible: false,
            Nodes: []
          },
          {
            Key: "roles",
            Description: "Roles & Permisos",
            Icon: "",
            Route: "/roles",
            Visible: false,
            Nodes: []
          },
          {
            Key: "connection",
            Description: "Conexiones",
            Icon: "",
            Route: "/connections",
            Visible: false,
            Nodes: []
          },
          {
            Key: "companies",
            Description: "Compañías",
            Icon: "",
            Route: "/companies",
            Visible: false,
            Nodes: []
          }
        ]
      },
      {
        Key: "logout",
        Description: "Cerrar sesión",
        Icon: "logout",
        Route: "/login",
        Visible: true,
        Nodes: []
      },
    ];
  }

  /**
   * Lifecycle hook that is called after the component is initialized.
   * It loads user data, permissions, and registers menu event handlers.
   */
  ngOnInit(): void {
    this.User = this.dataStorageService.GetItem<IToken>('currentUser');
    this.GetPermissionsByRol();
    Register(this.MenuId, CL_CHANNEL.OUTPUT, this.ClickMenuOption, this.callbacks);
    this.linkerService.Flow()?.pipe(
      StepDown<CL_CHANNEL>(this.callbacks),
    ).subscribe({
      next: callback => Run(callback.Target, callback, this.callbacks.Callbacks),
      error: error => console.log(`Error:`, error)
    });
    this.cdr.detectChanges();
    this.userImageAvailable = true;
  }

  /**
   * Handles errors when loading the user image.
   */
  handleImageError(): void {
    this.userImageAvailable = false;
  }

  /**
   * Retrieves permissions for the current user's role and updates menu visibility.
   */
  GetPermissionsByRol(): void {
    this.permsList.length = 0;
    this.permsService.GetPermissionsByRole(Number(this.User?.rolId)).subscribe({
      next: (response: ICLResponse<IPermission[]>) => {
        if (response.Data.length > 0) {
          this.dataStorageService.SetItem<IPermission[]>('permissions', response.Data);
          this.permsList = response.Data;
          this.HandleMenuPermission(this.permsList);
          this.linkerService.Publish({
            CallBack: CL_CHANNEL.INFLATE,
            Target: this.MenuId,
            Data: JSON.stringify(this.MenuNodes)
          } as ICLEvent);
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        this.alertService.ShowAlert({ HttpErrorResponse: error });
      }
    });
  }

  /**
   * Handles menu option click events.
   *
   * @param {ICLEvent} _event - The event triggered by a menu option click.
   */
  ClickMenuOption = (_event: ICLEvent): void => {
    const menuNode: IMenuNode = JSON.parse(_event.Data) as IMenuNode;
    switch (menuNode.Key) {
      case "logout":
      //  this.aut.logout();
    }
  }

  /**
   * Toggles the menu visibility.
   */
  ToggleMenu(): void {
    this.Toggle = !this.Toggle;
    this.linkerService.Publish({
      CallBack: CL_CHANNEL.DATA_LINE_1,
      Target: this.MenuId,
      Data: JSON.stringify(this.Toggle)
    } as ICLEvent);
  }

  /**
   * Updates menu visibility based on user permissions.
   *
   * @param {IPermission[]} permission - List of user permissions.
   */
  HandleMenuPermission(permission: IPermission[]): void {
    this.MenuNodes.forEach((node: IMenuNode) => {
      if (node.Key === 'home' || node.Key === 'logout') {
        node.Visible = true;
      } else {
        node.Visible = this.CheckPermission(this.GetRequiredPermission(node.Key), permission);
      }
      if (node.Nodes.length > 0) {
        node.Nodes.forEach((subNode: IMenuNode) => {
          subNode.Visible = this.CheckPermission(this.GetRequiredPermission(subNode.Key), permission);
        });
      }
    });
  }

  /**
   * Checks if a specific permission is present in the user's permissions.
   *
   * @param {string} Key - The permission key to check.
   * @param {IPermission[]} permission - List of user permissions.
   * @returns {boolean} True if the permission is present, false otherwise.
   */
  CheckPermission(Key: string, permission: IPermission[]): boolean {
    return permission.some((perm: IPermission) => perm.Name === Key);
  }

  /**
   * Retrieves the required permission key for a menu item.
   *
   * @param {string} key - The menu item key.
   * @returns {string} The required permission key.
   */
  GetRequiredPermission(key: string): string {
    let requiredPerm: string = '';
    switch (key) {
      case 'settings':
        requiredPerm = 'M_Settings';
        break;
      case 'users':
        requiredPerm = 'S_Users';
        break;
      case 'roles':
        requiredPerm = 'S_RolsPerm';
        break;
      case 'connection':
        requiredPerm = 'S_Connections';
        break;
      case 'companies':
        requiredPerm = 'S_Companies';
        break;
      case 'documents':
        requiredPerm = 'M_Documents';
        break;
      case 'documents-list':
        requiredPerm = 'S_DocumentsList';
        break;
      case 'logs':
        requiredPerm = 'M_Logs';
        break;
      case 'logs-documents':
        requiredPerm = 'S_LogsList';
        break;
      case 'logsViewer':
        requiredPerm = 'S_PhysicalLogs';
        break;
    }
    return requiredPerm;
  }
}
